import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";

export default function MutualFunds() {
  const [loader, setLoader] = useState(false);
  const [funds, setFunds] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");

  const getFundsData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/mutualfund`,
      );
      if (res.status === 200) {
        setFunds(res.data.data);
      } else {
        setFunds([]);
      }
    } catch (err) {
      console.log(err);
      setLoader(true);
    }
  };

  useEffect(() => {
    getFundsData();
  }, []);

  useEffect(() => {
    const filtered = filterDataBySearch(funds ?? []);
    setFilteredData(filtered);
    setCurrentPage(0); // reset page after filter
  }, [funds, searchQuery, categoryFilter, subCategoryFilter]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const categories = [...new Set(funds.map((item) => item.category))];
  const subCategories = [
    ...new Set(
      funds
        .filter((item) =>
          categoryFilter ? item.category === categoryFilter : true,
        )
        .map((item) => item.sub_category),
    ),
  ];

  const filterDataBySearch = (data) => {
    return data.filter((item) => {
      const nameMatch =
        typeof item.schemes === "string" &&
        item.schemes.toLowerCase().includes(searchQuery.toLowerCase());

      const categoryMatch =
        typeof item.category === "string" &&
        item.category.toLowerCase().includes(searchQuery.toLowerCase());

      const textMatch = nameMatch || categoryMatch;

      const categoryFilterMatch = categoryFilter
        ? item.category === categoryFilter
        : true;
      const subCategoryFilterMatch = subCategoryFilter
        ? item.sub_category === subCategoryFilter
        : true;

      return textMatch && categoryFilterMatch && subCategoryFilterMatch;
    });
  };

  const indexOfLastItem = (currentPage + 1) * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const pageCount = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  // Delete
  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You want to delete this Mutual Fund",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#5000C0",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (result.isConfirmed) {
        const res = await axios.delete(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/mutualfund/delete/` + id,
        );
        if (res.status === 200) {
          getFundsData();
          Swal.fire({
            title: "Deleted!",
            text: "This Mutual Fund has been deleted.",
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          Swal.fire({
            title: "Deletion failed!",
            text: "Mutual Fund deletion failed.",
            icon: "error",
          });
        }
      }
    } catch (err) {
      console.error("Error deleting data:", err);
      setLoader(true);
    }
  };

  if (loader) {
    return (
      <div className="page-wrapper page-settings">
        <div className="error">
          <h3>Something went wrong. Please try again.</h3>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="main-content">
        {!loader && (
          <div className="page-wrapper page-settings">
            <div className="content">
              {/* Breadcrumb and Header Section with Card Shadow */}
              <div
                className="mb-4"
                style={{
                  background: "#ffffff",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  padding: "20px",
                }}
              >
                <div className="content-page-header content-page-headersplit">
                  <h5>Mutual Funds</h5>
                </div>
                <nav
                  aria-label="breadcrumb"
                  style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
                >
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/dashboard">Dashboard</NavLink> /
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Mutual Funds
                    </li>
                  </ol>
                </nav>
              </div>

              {/* Filters Section with Card Shadow */}
              <div
                className="mb-4"
                style={{
                  background: "#ffffff",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  padding: "20px",
                }}
              >
                <div className="d-flex justify-content-between align-items-center flex-wrap"> {/* Filters */}
                  <div className="row g-2 flex-grow-1">
                    {/* Category */}
                    <div className="col-12 col-md-3">
                      <select
                        className="form-control"
                        value={categoryFilter}
                        onChange={(e) => {
                          setCategoryFilter(e.target.value);
                          setSubCategoryFilter("");
                          setCurrentPage(0);
                        }}
                      >
                        <option value="">All Categories</option>
                        {categories.map((cat, index) => (
                          <option key={index} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* SubCategory */}
                    <div className="col-12 col-md-3">
                      <select
                        className="form-control"
                        value={subCategoryFilter}
                        onChange={(e) => {
                          setSubCategoryFilter(e.target.value);
                          setCurrentPage(0);
                        }}
                        disabled={!categoryFilter}
                      >
                        <option value="">All Subcategories</option>
                        {subCategories.map((sub, index) => (
                          <option key={index} value={sub}>
                            {sub}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Search */}
                    <div className="col-12 col-md-4">
                      <input
                        className="form-control"
                        type="text"
                        placeholder="Search by scheme or category..."
                        value={searchQuery}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>
                  <div className="list-btn">
                    <Link to="/create-mutualfund" className="btn btn-primary">
                      <i className="fas fa-plus me-2"></i>
                      Create
                    </Link>
                  </div>
                </div>
              </div>

              {/* Table Section with Card Shadow */}
              <div
                className="mb-4"
                style={{
                  background: "#ffffff",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  padding: "20px",
                }}
              >
                <div className="row">
                  <div className="col-12">
                    <div className="table-responsive">
                      <table className="table datatable">
                        <thead>
                          <tr>
                            <th>Id</th>
                            <th>Scheme</th>
                            <th>Category</th>
                            <th>Sub Category</th>
                            <th>Expense Ratio</th>
                            <th>AUM (Cr)</th>
                            <th>1 Day</th>
                            <th>7 Day</th>
                            <th>15 Day</th>
                            <th>30 Day</th>
                            <th>3 Month</th>
                            <th>6 Month</th>
                            <th>1 Year</th>
                            <th>2 Year</th>
                            <th>3 Year</th>
                            <th>5 Year</th>
                            <th>7 Year</th>
                            <th>10 Year</th>
                            <th>15 Year</th>
                            <th>20 Year</th>
                            <th>25 Year</th>
                            <th>Since Inception</th>
                            <th>Rating</th>
                            <th>Alpha</th>
                            <th>Beta</th>
                            <th>Mean</th>
                            <th>Std Dev</th>
                            <th>Sharpe</th>
                            <th>Sortino</th>
                            <th>Avg Maturity</th>
                            <th>Modified Duration</th>
                            <th>Yield to Maturity</th>
                            <th>Launch Date</th>
                            <th>Benchmark</th>
                            <th>Largecap</th>
                            <th>Midcap</th>
                            <th>Smallcap</th>
                            <th>NAV</th>
                            <th>Recommended</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.length === 0 ? (
                            <tr>
                              <td colSpan="39">No data available</td>
                            </tr>
                          ) : (
                            currentItems.map((dataa, index) => (
                              <tr key={index}>
                                <td>{indexOfFirstItem + index + 1}</td>
                                <td>{dataa.schemes}</td>
                                <td>{dataa.category}</td>
                                <td>{dataa.sub_category}</td>
                                <td>{dataa.expense_ratio}</td>
                                <td>{dataa.aum_cr}</td>
                                <td>{dataa.one_day ?? "-"}</td>
                                <td>{dataa.seven_day ?? "-"}</td>
                                <td>{dataa.fifteen_day ?? "-"}</td>
                                <td>{dataa.thirty_day ?? "-"}</td>
                                <td>{dataa.three_month ?? "-"}</td>
                                <td>{dataa.six_month ?? "-"}</td>
                                <td>{dataa.one_year ?? "-"}</td>
                                <td>{dataa.two_year ?? "-"}</td>
                                <td>{dataa.three_year ?? "-"}</td>
                                <td>{dataa.five_year ?? "-"}</td>
                                <td>{dataa.seven_year ?? "-"}</td>
                                <td>{dataa.ten_year ?? "-"}</td>
                                <td>{dataa.fifteen_year ?? "-"}</td>
                                <td>{dataa.twenty_year ?? "-"}</td>
                                <td>{dataa.twentyfive_year ?? "-"}</td>
                                <td>{dataa.since_inception_return ?? "-"}</td>
                                <td>{dataa.fund_rating}</td>
                                <td>{dataa.alpha ?? "-"}</td>
                                <td>{dataa.beta ?? "-"}</td>
                                <td>{dataa.mean ?? "-"}</td>
                                <td>{dataa.standard_dev ?? "-"}</td>
                                <td>{dataa.sharpe_ratio ?? "-"}</td>
                                <td>{dataa.sortino_ratio ?? "-"}</td>
                                <td>{dataa.average_maturity ?? "-"}</td>
                                <td>{dataa.modified_duration ?? "-"}</td>
                                <td>{dataa.yield_to_maturity ?? "-"}</td>
                                <td>{dataa.launch_date}</td>
                                <td>{dataa.scheme_benchmark}</td>
                                <td>{dataa.largecap_ratio}</td>
                                <td>{dataa.midcap_ratio}</td>
                                <td>{dataa.smallcap_ratio}</td>
                                <td>{dataa.current_nav}</td>
                                <td>{dataa.is_recommended}</td>
                                <td>
                                  <div className="table-actions d-flex justify-content-center">
                                    <Link
                                      className="delete-table me-2"
                                      to={`/edit-mutualfund/${dataa.id}`}
                                      title="Edit Mutual Fund"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </Link>
                                    <div
                                      className="delete-table"
                                      onClick={() => handleDelete(dataa.id)}
                                      title="Delete Mutual Fund"
                                      style={{ cursor: "pointer" }}
                                    >
                                      <i className="fas fa-trash-alt"></i>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Pagination */}
                {pageCount > 1 && (
                  <nav className="mt-4 pagination justify-content-end gap-2">
                    <select
                      className="form-select"
                      style={{ width: "70px", padding: "0 13px" }}
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="30">30</option>
                      <option value="40">40</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                    <ReactPaginate
                      className="pagination justify-content-center"
                      previousLabel={"Previous"}
                      nextLabel={"Next"}
                      pageCount={pageCount}
                      pageClassName={"page-link"}
                      onPageChange={handlePageClick}
                      containerClassName={"pagination"}
                      previousLinkClassName={"page-link"}
                      nextLinkClassName={"page-link"}
                      disabledClassName={"disabled"}
                      activeClassName={"active"}
                    />
                  </nav>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
